import {QueryClient} from '@tanstack/react-query';

/**
 * Smart refresh after assistant edits: maps each MUTATING assistant tool name
 * to the react-query cache keys its success invalidates, so pages showing that
 * data refetch while the user watches the chat. Read-only tools are absent on
 * purpose. Lives in the host app (not src/assistant) so the assistant module
 * stays free of app-specific query keys — it is wired through
 * AssistantIntegration.onToolCallCompleted in AppRoutes.
 *
 * Note: the main allocation board (ResourceAllocationContext) is NOT
 * react-query — it already refreshes via the ResourceAllocationHub SignalR
 * broadcasts the service layer emits. The keys here cover the react-query
 * caches those broadcasts don't reach.
 */

// Person/team detail pages, availability panels, directory + picker searches.
const ALLOCATION_KEYS: string[][] = [['resource-allocation']];

// Document detail views (by group+path and by id).
const DOCUMENT_DETAIL_KEYS: string[][] = [
    ['markdownDocument'],
    ['markdownDocumentById']
];

// Document lists: paged admin table, directory tree, path lookups.
const DOCUMENT_LIST_KEYS: string[][] = [
    ['pagedMarkdownDocuments'],
    ['markdownDocumentsByDirectory'],
    ['documentPaths']
];

const TOOL_QUERY_KEYS: Record<string, string[][]> = {
    // Allocations
    add_allocation: ALLOCATION_KEYS,
    update_allocation: ALLOCATION_KEYS,
    delete_allocation: ALLOCATION_KEYS,

    // Single-record tools: every planning record lives under 'resource-allocation'
    ...Object.fromEntries(
        [
            'create_project',
            'update_project',
            'shift_project',
            'delete_project',
            'set_project_access',
            'remove_project_access',
            'save_project_phase',
            'delete_project_phase',
            'save_assignment',
            'delete_assignment',
            'save_person',
            'delete_person',
            'save_team',
            'delete_team',
            'save_job_role',
            'delete_job_role',
            'save_tag',
            'delete_tag',
            'save_closure',
            'delete_closure',
            'save_availability',
            'delete_availability',
            'set_availability_week',
            'clear_availability_weeks',
            'update_working_hours',
            'update_rag_thresholds',
            'update_utilisation_settings',
            'delete_config_version'
        ].map(tool => [tool, ALLOCATION_KEYS])
    ),

    // Imports (any record type; the board itself refreshes via the RefetchState broadcast)
    commit_import: [...ALLOCATION_KEYS, ['imports']],

    // Documents (documentation assistant)
    create_document: [...DOCUMENT_LIST_KEYS],
    replace_in_document: [...DOCUMENT_DETAIL_KEYS, ['pagedMarkdownDocuments']],
    write_document_content: [
        ...DOCUMENT_DETAIL_KEYS,
        ['pagedMarkdownDocuments']
    ],
    update_document_metadata: [...DOCUMENT_DETAIL_KEYS, ...DOCUMENT_LIST_KEYS],
    publish_document: [
        ...DOCUMENT_DETAIL_KEYS,
        ['pagedMarkdownDocuments'],
        ['markdownDocumentVersions']
    ],
    discard_document_draft: [
        ...DOCUMENT_DETAIL_KEYS,
        ['pagedMarkdownDocuments']
    ],
    delete_document: [...DOCUMENT_DETAIL_KEYS, ...DOCUMENT_LIST_KEYS]
};

/** Invalidate the caches touched by a completed assistant tool call. */
export function refreshAfterAssistantTool(
    queryClient: QueryClient,
    toolName: string
): void {
    const keys = TOOL_QUERY_KEYS[toolName];
    if (!keys) return;
    for (const queryKey of keys) {
        void queryClient.invalidateQueries({queryKey});
    }
}

/** Exported for tests. */
export const assistantToolQueryKeys = TOOL_QUERY_KEYS;
